'use client';

import { useMemo, useEffect } from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { Work } from '@/types/work';
import { WorkMetadata } from '@/services/metadata.service';
import { Comment } from '@/types/comment';
import { TabType } from './WorkTabs';
import { CommentFeed } from '@/components/Comment/CommentFeed';
import { PostBlockEditor } from './PostBlockEditor';
import { FundraiseProgress } from '@/components/Fund/FundraiseProgress';
import { useStorageKey } from '@/utils/storageKeys';
import { useUser } from '@/contexts/UserContext';
import { ReviewStatusBanner } from '@/components/Bounty/ReviewStatusBanner';
import { useShareModalContext } from '@/contexts/ShareContext';
import { useWorkTab } from './WorkHeader/WorkTabContext';

interface FundDocumentProps {
  work: Work;
  metadata: WorkMetadata;
  content?: string;
  authorUpdates?: Comment[];
}

export const FundDocument = ({
  work,
  metadata,
  content,
  authorUpdates = [],
}: FundDocumentProps) => {
  const { activeTab } = useWorkTab();
  const storageKey = useStorageKey('rh-comments');
  const { user } = useUser();
  const { showShareModal } = useShareModalContext();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // Check if current user is an author of the work
  const isCurrentUserAuthor = useMemo(() => {
    if (!user?.id) return false;
    return work.authors.some(
      (authorship) => authorship.authorProfile.id === user?.authorProfile?.id
    );
  }, [user?.id, work.authors]);

  useEffect(() => {
    const newParam = searchParams.get('new');
    if (newParam === 'true') {
      showShareModal({
        action: 'USER_OPENED_PROPOSAL',
        docTitle: work.title,
        url: `${window.location.origin}${pathname}`,
        shouldShowConfetti: true,
      });

      const url = new URL(window.location.href);
      url.searchParams.delete('new');
      router.replace(url.pathname + url.search, { scroll: false });
    }
  }, [searchParams, router, pathname, work.title, showShareModal]);

  // Render tab content based on activeTab
  const renderTabContent = useMemo(() => {
    switch (activeTab) {
      case 'paper':
        return (
          <div className="mt-6">
            {metadata.fundraising && (
              <div className="mb-6">
                <FundraiseProgress
                  fundraise={{
                    id: metadata.fundraising.id,
                    status: metadata.fundraising.status,
                    amountRaised: {
                      rsc: metadata.fundraising.amountRaised.rsc,
                      usd: metadata.fundraising.amountRaised.usd,
                    },
                    goalAmount: {
                      rsc: metadata.fundraising.goalAmount.rsc,
                      usd: metadata.fundraising.goalAmount.usd,
                    },
                    endDate: metadata.fundraising.endDate,
                    contributors: {
                      numContributors: metadata.fundraising.contributors.topContributors.length,
                      topContributors: metadata.fundraising.contributors.topContributors,
                    },
                    createdDate: metadata.fundraising.createdDate || '',
                    updatedDate: metadata.fundraising.updatedDate || '',
                    goalCurrency: metadata.fundraising.goalCurrency || 'RSC',
                  }}
                  fundraiseTitle={work.title}
                  work={work}
                  onContribute={() => {}}
                />
              </div>
            )}
            {work.previewContent ? (
              <PostBlockEditor content={work.previewContent} />
            ) : content ? (
              <div
                className="prose max-w-none bg-white rounded-lg shadow-sm border p-6 mb-6"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            ) : (
              <p className="text-gray-500">No content available</p>
            )}
          </div>
        );
      case 'updates':
        return (
          <div className="space-y-6" key="updates-tab">
            <CommentFeed
              documentId={work.id}
              unifiedDocumentId={work.unifiedDocumentId || null}
              contentType={work.contentType}
              commentType="AUTHOR_UPDATE"
              key={`update-feed-${work.id}`}
              hideEditor={!isCurrentUserAuthor}
              workAuthors={work.authors}
              editorProps={{
                placeholder: 'Write an update...',
                commentType: 'AUTHOR_UPDATE',
                storageKey: `${storageKey}-update-feed-${work.id}`,
              }}
              work={work}
            />
          </div>
        );
      case 'reviews':
        return (
          <div className="space-y-6" key="reviews-tab">
            <CommentFeed
              unifiedDocumentId={work.unifiedDocumentId || null}
              documentId={work.id}
              contentType={work.contentType}
              commentType="REVIEW"
              key={`review-feed-${work.id}`}
              workAuthors={work.authors}
              belowEditor={<ReviewStatusBanner bounties={metadata.bounties || []} />}
              editorProps={{
                placeholder: 'Write your review...',
                initialRating: 0,
                commentType: 'REVIEW',
                storageKey: `${storageKey}-review-feed-${work.id}`,
              }}
              work={work}
            />
          </div>
        );
      case 'bounties':
        return (
          <div className="space-y-6" key="bounties-tab">
            <CommentFeed
              unifiedDocumentId={work.unifiedDocumentId || null}
              documentId={work.id}
              contentType={work.contentType}
              commentType="BOUNTY"
              renderCommentActions={false}
              hideEditor={true}
              key={`bounty-feed-${work.id}`}
              workAuthors={work.authors}
              editorProps={{
                storageKey: `${storageKey}-bounty-feed-${work.id}`,
              }}
              work={work}
            />
          </div>
        );
      case 'conversation':
        return (
          <div className="space-y-6" key="conversation-tab">
            <CommentFeed
              unifiedDocumentId={work.unifiedDocumentId || null}
              documentId={work.id}
              contentType={work.contentType}
              commentType="GENERIC_COMMENT"
              key={`comment-feed-${work.id}`}
              workAuthors={work.authors}
              editorProps={{
                storageKey: `${storageKey}-comment-feed-${work.id}`,
              }}
              work={work}
            />
          </div>
        );
      default:
        return null;
    }
  }, [activeTab, work, metadata, content, storageKey, isCurrentUserAuthor]);

  return <div>{renderTabContent}</div>;
};
